import type { DemoState, Enrollment, Olympiad, Student } from './types';
import { formatClassName } from './classCode';
import { isPaidOlympiad } from './rules';
import { translateEnrollmentStatus, translatePaymentStatus } from './reportFormatting';

export interface ReportFilters { year: number; unitId: string; className: string; olympiadId: string; studentId?: string }
export const editionYear = (olympiad: Olympiad) => Number((olympiad.phases.find((phase) => phase.date)?.date || olympiad.enrollmentEnd).slice(0, 4));
const dateYear = (value: string) => Number(value?.slice(0, 4));
const money = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export function reportTable(id: string, data: DemoState, filters: ReportFilters): string[][] {
  // RLS defines access; this additional scope makes every report's dimensions explicit.
  const olympiads = data.olympiads.filter((o) => o.unitId === filters.unitId && (filters.olympiadId === 'all' || o.id === filters.olympiadId));
  const olympiadById = new Map(olympiads.map((o) => [o.id, o]));
  const studentById = new Map(data.students.filter((s) => s.unitId === filters.unitId).map((s) => [s.id, s]));
  const matchesStudent = (s: Student) => (filters.className === 'all' || s.className === filters.className)
    && (id !== 'student-history' || s.id === filters.studentId);
  const allEnrollments = data.enrollments.filter((e) => olympiadById.has(e.olympiadId));
  const enrollments = allEnrollments.filter((e) => dateYear(e.requestedAt) === filters.year);
  const people = enrollments.flatMap((e) => e.studentIds.flatMap((studentId) => {
    const student = studentById.get(studentId);
    return student && matchesStudent(student) ? [{ enrollment: e, student, olympiad: olympiadById.get(e.olympiadId)! }] : [];
  }));
  if (id === 'awards') {
    return [['Estudante', 'RA', 'Turma', 'Olimpíada', 'Fase', 'Nota', 'Classificação', 'Premiação', 'Publicação'],
      ...data.results.flatMap((r) => {
        const student = studentById.get(r.studentId), olympiad = olympiadById.get(r.olympiadId);
        const phase = olympiad?.phases.find((p) => p.id === r.phaseId);
        const year = phase?.date ? dateYear(phase.date) : olympiad ? editionYear(olympiad) : NaN;
        return student && olympiad && matchesStudent(student) && year === filters.year
          ? [[student.name, student.ra, formatClassName(student.className), olympiad.acronym, phase?.title ?? r.phaseId,
              r.score === null ? '' : String(r.score), r.classification, r.award, r.published ? 'Publicado' : 'Rascunho']] : [];
      })];
  }
  if (id === 'fiscal') {
    const rows: string[][] = [['Olimpíada', 'Tipo de cobrança', 'Referência', 'Escopo', 'Quantidade', 'Valor unitário', 'Total', 'Situação']];
    for (const o of olympiads) if (editionYear(o) === filters.year && o.schoolFeeEnabled && (o.schoolFeeAmount ?? 0) > 0)
      rows.push([o.acronym, 'Taxa do colégio', 'Institucional (não rateada por turma)', 'Única', '1', money(o.schoolFeeAmount!), money(o.schoolFeeAmount!), 'Prevista']);
    for (const e of allEnrollments) {
      if (e.status === 'cancelled' || !e.studentIds.some((studentId) => { const s = studentById.get(studentId); return s && matchesStudent(s); })) continue;
      const o = olympiadById.get(e.olympiadId)!;
      for (const p of e.payments ?? []) if (dateYear(p.dueDate || e.requestedAt) === filters.year)
        rows.push([o.acronym, 'Participação', p.phaseTitle, p.scope === 'team' || o.format === 'Equipe' ? 'Por equipe' : 'Por estudante', '1', money(p.amount), money(p.amount), translatePaymentStatus(p.status)]);
    }
    return rows;
  }
  if (id === 'participation' || id === 'annual' || id === 'class-participation') {
    const grouped = new Map<string, { olympiad: Olympiad; className: string; entries: Array<{ enrollment: Enrollment; student: Student }> }>();
    for (const item of people) {
      const className = id === 'class-participation' ? item.student.className : '';
      const key = `${item.olympiad.id}:${className}`;
      if (!grouped.has(key)) grouped.set(key, { olympiad: item.olympiad, className, entries: [] });
      grouped.get(key)!.entries.push(item);
    }
    const header = ['Olimpíada', ...(id === 'class-participation' ? ['Turma'] : []), 'Participações ativas (pessoas)', 'Confirmadas (pessoas)', 'Pendentes (pessoas)', 'Inscrições/equipes ativas', 'Canceladas (pessoas)'];
    return [header, ...[...grouped.values()].map((group) => {
      const active = group.entries.filter(({ enrollment }) => enrollment.status !== 'cancelled');
      return [group.olympiad.acronym, ...(id === 'class-participation' ? [formatClassName(group.className)] : []),
        String(active.length), String(active.filter(({ enrollment }) => enrollment.status === 'confirmed').length),
        String(active.filter(({ enrollment }) => enrollment.status !== 'confirmed').length),
        String(new Set(active.map(({ enrollment }) => enrollment.id)).size), String(group.entries.length - active.length)];
    })];
  }
  return [['Protocolo', 'Estudante', 'RA', 'Turma', 'Olimpíada', 'Inscrição', 'Pagamento'],
    ...people.map(({ enrollment: e, student: s, olympiad: o }) => [e.protocol, s.name, s.ra, formatClassName(s.className), o.acronym,
      translateEnrollmentStatus(e.status), translatePaymentStatus(e.paymentStatus, isPaidOlympiad(o))])];
}
